import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * ProtectedRoute - Enforces authentication and onboarding on routes
 * Redirects to /login if user is not authenticated
 * Redirects to /onboarding if user needs onboarding (except for /onboarding route)
 */
export function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, needsOnboarding } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-primary)'
      }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login, saving the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user needs onboarding (but allow access to onboarding route)
  if (needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

/**
 * PublicRoute - For pages that should only be accessible when NOT authenticated
 * (e.g., login page - redirect to dashboard if already logged in)
 */
export function PublicRoute({ children }) {
  const { isAuthenticated, loading, needsOnboarding } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-primary)'
      }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  if (isAuthenticated) {
    // If user needs onboarding, redirect to onboarding
    if (needsOnboarding) {
      return <Navigate to="/onboarding" replace />;
    }
    // Redirect to intended page or default to command centre
    const from = location.state?.from?.pathname || '/command-centre';
    return <Navigate to={from} replace />;
  }

  return children;
}

export default ProtectedRoute;
